<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="vatCategories"
      :headers="vatCategoriesHeaders"
      :list="vatCategories"
      clickable
      @add-new="onAddNewVatCategory"
      @table-item-clicked="onEditVatCategory"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData"
      table-id="vat-categories"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as VatCategory)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <VatCategoriesModalCreateEdit
      v-if="createEditModalActive"
      :mode="createEditModalMode"
      :vat-category="vatCategory"
      @close-modal="createEditModalActive = false"
      @save="onSave"></VatCategoriesModalCreateEdit>
    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{ $t('messages.info.deleteSingle', { 'item': deleteRowItem?.name }) }}
      </template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';

import { getVatCategories, removeVatCategoriesByIds } from '@/services/api/vat';
import { filterActive, filterData, sortData } from '@/utils/table';
import VatCategoriesModalCreateEdit from '@/components/prices/VatCategoriesModalCreateEdit.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';

import type { TableFilterData } from '@/types/Base';
import type { VatCategory } from '@/types/Vat';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();
const { reloadLookups } = useLookupsStore();

const vatCategories = ref<VatCategory[]>([]);
const filterIsActive = ref<boolean | undefined>();

const vatCategoriesHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'boid', text: $t('baseObject.boid'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

const vatCategory = ref<VatCategory>(initializeVatCategory());

const createEditModalActive = ref(false);

const createEditModalMode = ref<'add' | 'edit'>('add');

function initializeVatCategory(): VatCategory {
  return {
    id: uuidv4(),
    name: '',
    code: '',
    isActive: true,
    isFromManager: true,
  };
}

function onAddNewVatCategory() {
  vatCategory.value = initializeVatCategory();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEditVatCategory(item: VatCategory) {
  vatCategory.value = item;
  createEditModalActive.value = true;
  createEditModalMode.value = 'edit';
}

async function onSave(modifiedData: VatCategory) {
  if (!vatCategories.value) return;

  const index = vatCategories.value.findIndex((item) => item.id === modifiedData.id);
  if (index !== -1) vatCategories.value[index] = modifiedData;
  else vatCategories.value.push(modifiedData);
  createEditModalActive.value = false;

  await reloadLookups(['vatCategories']);
}

async function loadData(data?: TableFilterData) {
  let response = await getVatCategories();
  if (!response) return;

  if (data && data.query) {
    response = filterData(response, data.query, ['name', 'code']);
  }

  if (data && data.column !== undefined && data.order) {
    response = sortData(response, data.column, data.order, vatCategoriesHeaders);
  }

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);

  vatCategories.value = response;
}

async function onDeleteMultiselect(items: VatCategory[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  const res = await removeVatCategoriesByIds(idsToDelete);
  if (!res && vatCategories.value)
    vatCategories.value = vatCategories.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['vatCategories']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeVatCategoriesByIds([idToDelete]);
  if (!res && vatCategories.value)
    vatCategories.value = vatCategories.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['vatCategories']);
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<VatCategory>();
function onDeleteRow(item: VatCategory) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
